/**
 * Skin Salt — Freebie Guard
 * =========================
 * Client-side watchdog that auto-removes "orphan freebies" from the cart.
 * See docs/freebie-safeguards.md for the full design.
 *
 * Diagnostics:
 *   On init, logs the loaded config so you can verify Smart Collections
 *   are populating correctly. On any orphan-removal pass, logs which
 *   line items were removed.
 *
 *   In console:
 *     window.SkinSaltFreebieGuard          → see config
 *     window.SkinSaltFreebieGuard.validateCart()  → run guard manually
 */

(function () {
  'use strict';

  var TAG = '[FreebieGuard]';
  var cfg = window.SkinSaltFreebieGuard;

  if (!cfg) {
    console.warn(TAG, 'window.SkinSaltFreebieGuard not found — snippet not loaded?');
    return;
  }

  console.log(TAG, 'init', {
    freebieVariantIds: cfg.freebieVariantIds,
    qualifyingBoxVariantIds: cfg.qualifyingBoxVariantIds,
    freebieRules: cfg.freebieRules
  });

  if (!Array.isArray(cfg.freebieVariantIds) || cfg.freebieVariantIds.length === 0) {
    console.warn(TAG, 'freebieVariantIds is empty — check Smart Collection `freebies` exists ' +
      'and contains products tagged `freebie`. Guard is a no-op until this is fixed.');
    return;
  }

  /* Use the original fetch internally so the guard's own /cart calls don't
     re-trigger the patched fetch wrapper below (would cause an infinite loop). */
  var origFetch = window.fetch.bind(window);

  /* Build O(1) lookup sets. Cart.items uses numeric variant_id, so we
     normalize everything to strings for safe ===. */
  var freebieSet = {};
  cfg.freebieVariantIds.forEach(function (id) { freebieSet[String(id)] = true; });

  var qualifyingSet = {};
  (cfg.qualifyingBoxVariantIds || []).forEach(function (id) { qualifyingSet[String(id)] = true; });

  /* Per-freebie rules: freebie variant ID → [allowed qualifying box variant IDs].
     Empty array means "no specific rules — fall back to qualifyingSet check". */
  var freebieRules = cfg.freebieRules || {};

  /* Warn on freebies with no specific rules — they fall back to the looser
     "any qualifying box" check, which can leak across tiers. */
  Object.keys(freebieRules).forEach(function (vid) {
    if (!Array.isArray(freebieRules[vid]) || freebieRules[vid].length === 0) {
      console.warn(TAG, 'freebie variant', vid, 'has no `freebie-for:<box-handle>` tags — ' +
        'falling back to "any qualifying box" check. Add per-tier tags for stricter enforcement.');
    }
  });

  var inFlight = false; /* Re-entrancy guard so a removal doesn't trigger another check */

  /**
   * Fetch current cart, identify orphan freebies, remove them if any,
   * and dispatch `skinsalt:freebie-guard:removed` (with the updated cart
   * payload) so listeners (like the cart drawer) can re-render.
   *
   * Returns Promise<{ didRemove: boolean, cart: object|null }>.
   */
  function validateCart() {
    if (inFlight) return Promise.resolve({ didRemove: false, cart: null });
    inFlight = true;

    return origFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) {
          console.log(TAG, 'no cart items — skipping');
          return { didRemove: false, cart: cart };
        }

        /* Build cart-side lookups in one pass */
        var cartVariantIds = {};
        var cartVariantQty = {}; // vid → total quantity across lines (for minQty rules)
        var freebieLinesInCart = []; // [{ key, vid, name }, ...]
        cart.items.forEach(function (item) {
          var vid = String(item.variant_id);
          cartVariantIds[vid] = true;
          cartVariantQty[vid] = (cartVariantQty[vid] || 0) + (item.quantity || 0);
          if (freebieSet[vid]) {
            freebieLinesInCart.push({
              key: item.key,
              vid: vid,
              name: item.product_title || item.title
            });
          }
        });

        /* For each freebie in cart, determine if it's an orphan.
           Strict: if freebie has `freebie-for:` rules → at least one rule must
                   be satisfied. A rule is either:
                     - a variant ID string → that box variant must be in cart
                       (presence check — control's per-product tiers), or
                     - { variants: [ids], minQty: N } → total cart quantity
                       across those variants must be ≥ N (v7's quantity tiers,
                       from `freebie-for:<handle>:<minQty>` tags).
           Fallback: if freebie has no rules → any `freebie-bundle-trigger`
                     box variant in cart authorizes it. */
        var orphanFreebieKeys = [];
        var orphanFreebieNames = [];
        var freebieDecisions = []; // for diagnostic logging

        function ruleSatisfied(rule) {
          if (typeof rule === 'string') return !!cartVariantIds[rule];
          if (rule && rule.variants) {
            var total = 0;
            rule.variants.forEach(function (vid) { total += cartVariantQty[String(vid)] || 0; });
            return total >= (rule.minQty || 1);
          }
          return false;
        }

        freebieLinesInCart.forEach(function (fl) {
          var rules = freebieRules[fl.vid];
          var allowed = false;
          var basis = '';

          if (rules && rules.length > 0) {
            /* Strict per-tier check */
            allowed = rules.some(ruleSatisfied);
            basis = allowed ? 'matched specific rule' : 'no matching specific box (or below min quantity)';
          } else {
            /* Fallback: any qualifying box authorizes */
            allowed = Object.keys(cartVariantIds).some(function (vid) { return qualifyingSet[vid]; });
            basis = allowed ? 'matched fallback (any qualifying box)' : 'no qualifying box at all';
          }

          freebieDecisions.push({ name: fl.name, allowed: allowed, basis: basis });

          if (!allowed) {
            orphanFreebieKeys.push(fl.key);
            orphanFreebieNames.push(fl.name);
          }
        });

        console.log(TAG, 'check', {
          freebies: freebieDecisions,
          willRemove: orphanFreebieNames
        });

        if (orphanFreebieKeys.length === 0) {
          return { didRemove: false, cart: cart };
        }

        /* Remove all orphan freebies in one batch */
        var updates = {};
        orphanFreebieKeys.forEach(function (key) { updates[key] = 0; });

        return origFetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) { return r.json(); })
          .then(function (newCart) {
            console.log(TAG, 'removed orphan freebies', orphanFreebieNames);

            /* Notify listeners (cart drawer, cart page handler) with updated cart */
            try {
              document.dispatchEvent(new CustomEvent('skinsalt:freebie-guard:removed', {
                detail: { removedKeys: orphanFreebieKeys, cart: newCart }
              }));
            } catch (e) { /* IE compat — ignore */ }

            return { didRemove: true, cart: newCart };
          });
      })
      .catch(function (err) {
        console.warn(TAG, 'validation failed:', err);
        return { didRemove: false, cart: null };
      })
      .then(function (result) {
        inFlight = false;
        return result;
      });
  }

  /* ── 1. Run on every page load ───────────────────────────────────────
     Catches the URL exploit: user visits /cart/add?id=<freebie> then loads
     any other page — guard fires and removes the orphan. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', validateCart);
  } else {
    validateCart();
  }

  /* ── 2. Patch fetch to re-validate after any cart mutation ──────────
     Theme code uses /cart/add.js, /cart/change.js, /cart/update.js.
     After any of those return, run the guard. */
  var origFetchPatched = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)\.js/.test(url);
    var promise = origFetchPatched.apply(this, arguments);
    if (isCartMutation) {
      promise.then(function () {
        /* Slight delay so the cart state has settled in Shopify's session
           before we read /cart.js. Empirically 50–100ms is plenty. */
        setTimeout(validateCart, 100);
      }).catch(function () { /* swallow — original caller handles it */ });
    }
    return promise;
  };

  /* ── 3. Patch XHR for the same reason — older code paths may use XHR ─ */
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var XHR = window.XMLHttpRequest.prototype;
    var origOpen = XHR.open;
    var origSend = XHR.send;
    XHR.open = function (method, url) {
      this._fg_url = url || '';
      return origOpen.apply(this, arguments);
    };
    XHR.send = function () {
      var self = this;
      var url = self._fg_url || '';
      if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
        self.addEventListener('loadend', function () { setTimeout(validateCart, 100); });
      }
      return origSend.apply(self, arguments);
    };
  }

  /* Expose for manual triggering / debugging. */
  window.SkinSaltFreebieGuard.validateCart = validateCart;
})();
